import { useMediaQuery } from '@mui/material';
import { Children, cloneElement, memo, useMemo } from 'react';

import type { Theme } from '@mui/material/styles';
import type { FC, ReactElement } from 'react';

export interface ResponsivePropsProps {
  children: ReactElement;
  xs?: Record<string, unknown>;
  sm?: Record<string, unknown>;
  md?: Record<string, unknown>;
  lg?: Record<string, unknown>;
  xl?: Record<string, unknown>;
}

const defaultProps = {
  xs: {},
  sm: {},
  md: {},
  lg: {},
  xl: {},
};

export const ResponsiveProps: FC<ResponsivePropsProps> = memo(
  ({ children, ...props }) => {
    props = { ...defaultProps, ...props };
    const isExtraSmall = useMediaQuery((theme: Theme) =>
      theme.breakpoints.up('xs')
    );
    const isSmall = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));
    const isMedium = useMediaQuery((theme: Theme) =>
      theme.breakpoints.up('md')
    );
    const isLarge = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));
    const isExtraLarge = useMediaQuery((theme: Theme) =>
      theme.breakpoints.up('xl')
    );

    // eslint-disable-next-line max-statements
    const responsiveProps = useMemo((): Record<string, unknown> | undefined => {
      if (isExtraLarge) {
        return props.xl;
      }
      if (isLarge) {
        return props.lg;
      }
      if (isMedium) {
        return props.md;
      }
      if (isSmall) {
        return props.sm;
      }
      if (isExtraSmall) {
        return props.xs;
      }

      return {};
    }, [props, isExtraSmall, isExtraLarge, isSmall, isMedium, isLarge]);

    return (
      <>
        {Children.map(children, (child, index) =>
          cloneElement(child, { key: index, ...responsiveProps })
        )}
      </>
    );
  }
);
